import { useState, useEffect, useRef } from 'react';
import { LogoMark } from '../components/LogoMark.jsx';

/**
 * TopBar — navy brand strip with CritWise mark, project selector / user chip,
 * tier badge, and Download PDF button. When navigating on narrow viewports the
 * user menu collapses behind a hamburger.
 *
 * When `user` is supplied, the project selector is replaced by the user's
 * name and organization alongside a logout button. Leaving `user` undefined
 * preserves the legacy project-selector rendering so pre-auth tests and the
 * `?report=<id>` URL flow continue to work.
 */
export function TopBar({ projectName, reportId, user, organization, onLogout, navigate }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [menuOpen]);

  const label = projectName
    ? `${projectName}${reportId ? ` · ${reportId}` : ''}`
    : 'No project loaded';

  const downloadHref = reportId
    ? `/api/report/${encodeURIComponent(reportId)}/pdf`
    : null;

  const tier = organization?.subscription_tier;
  // PRESHIP-POLISH: platform owners show an OWNER badge instead of their
  // underlying plan (owners sit on a free-tier org). Display only — the
  // server-side owner bypass is unchanged.
  const isOwner = user?.is_owner === true;
  const badgeClass = isOwner ? 'tier-badge tier-owner' : `tier-badge tier-${tier}`;
  const badgeLabel = isOwner ? 'OWNER' : (tier ? tier.toUpperCase() : '');
  const showBadge = isOwner || !!tier;
  const isAdmin = user?.role === 'admin';
  const subStatus = organization?.subscription_status;
  const showSubscriptionBanner =
    subStatus === 'expired' || subStatus === 'read_only';

  const handleNav = (path) => {
    setMenuOpen(false);
    if (navigate) navigate(path);
    else window.location.assign(path);
  };

  return (
    <>
    {showSubscriptionBanner && (
      <SubscriptionStateBanner state={subStatus} navigate={navigate} />
    )}
    <header className="topbar" role="banner">
      <div className="topbar-brand">
        <LogoMark size={42} />
        <div className="topbar-brand-text">
          <span className="topbar-wordmark">CRITWISE</span>
          <span className="topbar-tagline">SCHEDULE INTELLIGENCE</span>
        </div>
      </div>

      <div className="topbar-actions">
        {user ? (
          <>
            <div className="topbar-user topbar-user-wide">
              {showBadge && (
                <span className={badgeClass} aria-label={isOwner ? 'Platform owner' : `Subscription: ${tier}`}>
                  {badgeLabel}
                </span>
              )}
              <span className="topbar-user-text">
                {user.full_name}
                {organization?.name ? <> <span className="topbar-user-sep">·</span> {organization.name}</> : null}
              </span>
              {navigate && (
                <nav className="topbar-nav" aria-label="Primary">
                  <button type="button" className="topbar-nav-link" onClick={() => handleNav('/dashboard')}>
                    Dashboard
                  </button>
                  {isAdmin && (
                    <button type="button" className="topbar-nav-link" onClick={() => handleNav('/team')}>
                      Team
                    </button>
                  )}
                  <button type="button" className="topbar-nav-link" onClick={() => handleNav('/settings')}>
                    Settings
                  </button>
                  {/* LAUNCH-OPS D: platform-owner oversight entry — owner only. */}
                  {isOwner && (
                    <button type="button" className="topbar-nav-link" onClick={() => handleNav('/admin')}>
                      Oversight
                    </button>
                  )}
                </nav>
              )}
              {onLogout && (
                <button
                  type="button"
                  className="topbar-logout"
                  onClick={onLogout}
                >
                  Log out
                </button>
              )}
            </div>
            <div className="topbar-user-compact" ref={menuRef}>
              <button
                type="button"
                className="topbar-hamburger"
                aria-expanded={menuOpen}
                aria-controls="topbar-compact-menu"
                aria-label="Menu"
                onClick={() => setMenuOpen((v) => !v)}
              >
                <span aria-hidden="true">☰</span>
              </button>
              {menuOpen && (
                <div id="topbar-compact-menu" className="topbar-compact-menu" role="menu">
                  {showBadge && (
                    <div className="topbar-compact-item topbar-compact-tier">
                      <span className={badgeClass}>{badgeLabel}</span>
                    </div>
                  )}
                  <div className="topbar-compact-item">
                    {user.full_name}
                    {organization?.name ? <div className="topbar-compact-org">{organization.name}</div> : null}
                  </div>
                  {navigate && (
                    <>
                      <button type="button" className="topbar-compact-link" role="menuitem"
                        onClick={() => handleNav('/dashboard')}>
                        Dashboard
                      </button>
                      {isAdmin && (
                        <button type="button" className="topbar-compact-link" role="menuitem"
                          onClick={() => handleNav('/team')}>
                          Team
                        </button>
                      )}
                      <button type="button" className="topbar-compact-link" role="menuitem"
                        onClick={() => handleNav('/settings')}>
                        Settings
                      </button>
                      {/* MOBILE-FIT M6 (v3.34): the owner-only Oversight entry,
                          mirrored into the narrow menu (closes the D4 item). */}
                      {isOwner && (
                        <button type="button" className="topbar-compact-link" role="menuitem"
                          onClick={() => handleNav('/admin')}>
                          Oversight
                        </button>
                      )}
                    </>
                  )}
                  {onLogout && (
                    <button
                      type="button"
                      className="topbar-compact-link topbar-compact-logout"
                      role="menuitem"
                      onClick={() => { setMenuOpen(false); onLogout(); }}
                    >
                      Log out
                    </button>
                  )}
                </div>
              )}
            </div>
          </>
        ) : (
          <div
            className="project-selector"
            role="button"
            tabIndex={0}
            aria-label={`Current project: ${label}. Project switching is not yet available.`}
            aria-disabled="true"
          >
            <span>{label}</span>
            <span className="project-selector-caret" aria-hidden="true">▾</span>
          </div>
        )}

        {downloadHref ? (
          <a
            className="download-pdf-btn"
            href={downloadHref}
            download
            aria-label={`Download PDF report for ${reportId}`}
          >
            Download PDF
          </a>
        ) : !user ? (
          <>
            <button
              type="button"
              className="download-pdf-btn"
              disabled
              aria-describedby="download-pdf-hint"
            >
              Download PDF
            </button>
            <span id="download-pdf-hint" className="sr-only">
              Load a report to enable PDF download
            </span>
          </>
        ) : null}
      </div>
    </header>
    </>
  );
}

const RENEW_MAILTO =
  'mailto:hello@critwise.com?subject=CritWise%20Subscription%20Renewal&body=Hi%20CritWise%20team%2C%0A%0AI%27d%20like%20to%20renew%20my%20CritWise%20subscription.%20Please%20send%20me%20the%20payment%20details.%0A%0AOrganisation%3A%20[your%20org%20name]%0A%0AThanks.';

function SubscriptionStateBanner({ state, navigate }) {
  const headline =
    state === 'expired'
      ? 'Your subscription has expired.'
      : 'Your subscription is in read-only mode.';
  const body =
    state === 'expired'
      ? 'Renew within the grace period to keep CritWise active. After the grace period, your account will be limited to read-only access.'
      : 'You can view past reports but cannot upload new schedules, invite teammates, or change settings.';

  const onCancel = (e) => {
    e.preventDefault();
    if (navigate) navigate('/settings', 'section=danger');
    else window.location.assign('/settings?section=danger');
  };

  return (
    <div className="subscription-state-banner" role="status" data-state={state}>
      <div className="subscription-state-banner-text">
        <strong>{headline}</strong> <span>{body}</span>
      </div>
      <div className="subscription-state-banner-actions">
        <a
          className="subscription-state-banner-cta primary"
          href={RENEW_MAILTO}
        >
          Renew subscription
        </a>
        <a
          className="subscription-state-banner-cta secondary"
          href="/settings?section=danger"
          onClick={onCancel}
        >
          Export &amp; cancel
        </a>
      </div>
    </div>
  );
}
